/**
 * Augmentation selection applier - transforms state
 */
import { getAugmentation } from "../augmentations/registry";
import type { SelectAugmentationAction } from "../types/augmentation";
import type { Color } from "../types/common";
import { FILES, RANKS } from "../types/common";
import type { ChessState } from "../types/game-state";
import type { Piece } from "../types/piece";

/**
 * Apply INITIAL_PLACEMENT effects for one player's augmentation.
 * Mutates the deep-cloned state in place.
 */
function applyInitialPlacementsForColor(
  state: ChessState,
  color: Color,
  augmentationId: string,
): void {
  const augmentation = getAugmentation(augmentationId);
  if (!augmentation || augmentation.effect.type !== "INITIAL_PLACEMENT") return;

  const { placements } = augmentation.effect;

  placements.forEach((placement, i) => {
    const position =
      color === "WHITE" ? placement.whitePosition : placement.blackPosition;
    const pieceId = `${color.toLowerCase()}-${placement.pieceType.toLowerCase()}-extra-${i}`;

    const newPiece: Piece = {
      id: pieceId,
      type: placement.pieceType,
      color,
      position,
      hasMoved: placement.hasMoved,
    };

    state.pieces.push(newPiece);

    const fileIndex = FILES.indexOf(position.file);
    const rankIndex = RANKS.indexOf(position.rank);

    if (fileIndex >= 0 && rankIndex >= 0) {
      const col = state.board.squares[fileIndex];
      if (col?.[rankIndex]) {
        col[rankIndex] = { ...col[rankIndex]!, pieceId };
      }
    }
  });
}

/**
 * Apply an augmentation selection action to the game state
 * Returns a new state (pure function - does not mutate input)
 *
 * Selection flow:
 * 1. Store the selection as pending (hidden from opponent)
 * 2. Mark the player as having selected
 * 3. If both players have selected, reveal selections and transition to PLAYING
 */
export function applySelectAugmentation(
  state: ChessState,
  action: SelectAugmentationAction,
): ChessState {
  // Deep clone state
  const newState = JSON.parse(JSON.stringify(state)) as ChessState;

  // Ensure augmentation selection state exists
  if (!newState.augmentationSelection) {
    return newState;
  }

  // Store the pending selection and mark player as having selected
  if (action.player === "WHITE") {
    newState.augmentations.white.pendingSelection = action.augmentationId;
    newState.augmentationSelection.whiteSelected = true;
  } else {
    newState.augmentations.black.pendingSelection = action.augmentationId;
    newState.augmentationSelection.blackSelected = true;
  }

  // Check if both players have now selected
  const bothSelected =
    newState.augmentationSelection.whiteSelected &&
    newState.augmentationSelection.blackSelected;

  if (bothSelected) {
    // Reveal selections: move pending to active
    const whiteAugId = newState.augmentations.white.pendingSelection;
    const blackAugId = newState.augmentations.black.pendingSelection;

    if (whiteAugId) {
      newState.augmentations.white.active.push(whiteAugId);
      newState.augmentations.white.pendingSelection = null;
    }

    if (blackAugId) {
      newState.augmentations.black.active.push(blackAugId);
      newState.augmentations.black.pendingSelection = null;
    }

    // Apply INITIAL_PLACEMENT effects at game start (augmentationRound === 0)
    if (newState.augmentationRound === 0) {
      if (whiteAugId)
        applyInitialPlacementsForColor(newState, "WHITE", whiteAugId);
      if (blackAugId)
        applyInitialPlacementsForColor(newState, "BLACK", blackAugId);
    }

    // Clear augmentation selection state
    newState.augmentationSelection = null;

    // Increment augmentation round counter
    newState.augmentationRound = newState.augmentationRound + 1;

    // Transition to playing phase
    newState.phase = "PLAYING";
  }

  // Add action to history
  newState.actionHistory.push(action);

  // Update timestamp
  newState.updatedAt = new Date().toISOString();

  return newState;
}
